/**
 * Confidence Score Breakdown Component
 *
 * FERP CRITICAL: Detailed confidence analysis with multi-LLM ensemble comparison
 * Shows per-layer confidence scores and consensus/outlier visualization
 *
 * FERP Principles:
 * - Data-driven (all confidence scores from backend ChaosIngestionService)
 * - Multi-LLM transparency (GPT-4, Claude, Gemini ensemble)
 * - Life-critical (confidence thresholds trigger human review)
 * - Domain-agnostic (works across healthcare/finance/legal)
 *
 * Confidence Layers:
 * - overall: Composite confidence score
 * - business_metadata: Name, description, domain, category extraction
 * - ui_structure: Form steps, fields, validation rules
 * - primitive_mapping: 16 primitive type assignments
 * - field_mapping: UI ↔ Primitive bidirectional mappings
 * - compliance_detection: Regulatory framework identification
 */

import React, { useMemo } from 'react';
import {
  Stack,
  Card,
  Group,
  Text,
  Badge,
  Progress,
  RingProgress,
  Center,
  Table,
  ThemeIcon,
  Paper,
  Alert,
  Tooltip,
  ActionIcon,
  Grid,
  Divider,
} from '@mantine/core';
import {
  IconBrain,
  IconCheck,
  IconAlertCircle,
  IconTrendingUp,
  IconTrendingDown,
  IconUsers,
  IconShieldCheck,
  IconCode,
  IconLayout,
  IconFileText,
  IconInfoCircle,
} from '@tabler/icons-react';

/**
 * Confidence metrics interface
 */
export interface ConfidenceMetrics {
  overall: number;
  business_metadata: number;
  ui_structure: number;
  primitive_mapping: number;
  field_mapping: number;
  compliance_detection: number;
}

/**
 * Multi-LLM confidence data
 */
export interface MultiLLMConfidence {
  llm_name: string; // GPT-4, Claude, Gemini
  scores: ConfidenceMetrics;
  is_consensus: boolean; // Agrees with ensemble consensus
  deviations: { layer: string; deviation: number }[]; // Outlier layers
}

/**
 * Confidence layer metadata
 */
interface ConfidenceLayer {
  key: keyof ConfidenceMetrics;
  label: string;
  description: string;
  icon: React.ReactNode;
  threshold: number; // Minimum acceptable confidence
}

export interface ConfidenceScoreBreakdownProps {
  confidence: ConfidenceMetrics;
  multiLLMScores?: MultiLLMConfidence[]; // Optional multi-LLM comparison
  onLayerClick?: (layer: keyof ConfidenceMetrics) => void;
}

/**
 * Confidence Score Breakdown Component
 *
 * FERP Pattern: Per-layer confidence analysis with multi-LLM consensus
 */
export function ConfidenceScoreBreakdown({
  confidence,
  multiLLMScores,
  onLayerClick,
}: ConfidenceScoreBreakdownProps): JSX.Element {
  /**
   * Confidence layer definitions
   */
  const confidenceLayers: ConfidenceLayer[] = [
    {
      key: 'business_metadata',
      label: 'Business Metadata',
      description: 'Name, description, domain, category extraction accuracy',
      icon: <IconFileText size={16} />,
      threshold: 0.85,
    },
    {
      key: 'ui_structure',
      label: 'UI Structure',
      description: 'Form steps, fields, validation rules extraction',
      icon: <IconLayout size={16} />,
      threshold: 0.8,
    },
    {
      key: 'primitive_mapping',
      label: 'Primitive Mapping',
      description: '16 primitive type assignment accuracy',
      icon: <IconCode size={16} />,
      threshold: 0.9,
    },
    {
      key: 'field_mapping',
      label: 'Field Mapping',
      description: 'UI ↔ Primitive bidirectional mapping accuracy',
      icon: <IconTrendingUp size={16} />,
      threshold: 0.85,
    },
    {
      key: 'compliance_detection',
      label: 'Compliance Detection',
      description: 'Regulatory framework identification (HIPAA, SOX, FDA)',
      icon: <IconShieldCheck size={16} />,
      threshold: 0.9,
    },
  ];

  /**
   * Get confidence color
   */
  const getConfidenceColor = (score: number, threshold: number = 0.8): string => {
    if (score >= threshold) return 'green';
    if (score >= threshold - 0.1) return 'blue';
    if (score >= threshold - 0.2) return 'yellow';
    return 'red';
  };

  /**
   * Get confidence label
   */
  const getConfidenceLabel = (score: number): string => {
    if (score >= 0.9) return 'High';
    if (score >= 0.8) return 'Good';
    if (score >= 0.7) return 'Moderate';
    if (score >= 0.6) return 'Low';
    return 'Very Low';
  };

  /**
   * Calculate multi-LLM consensus
   */
  const consensusAnalysis = useMemo(() => {
    if (!multiLLMScores || multiLLMScores.length === 0) return null;

    const consensusCount = multiLLMScores.filter(llm => llm.is_consensus).length;
    const outlierCount = multiLLMScores.length - consensusCount;

    // Calculate average scores across all LLMs
    const avgScores: ConfidenceMetrics = {
      overall: 0,
      business_metadata: 0,
      ui_structure: 0,
      primitive_mapping: 0,
      field_mapping: 0,
      compliance_detection: 0,
    };

    multiLLMScores.forEach(llm => {
      Object.keys(avgScores).forEach(key => {
        avgScores[key as keyof ConfidenceMetrics] +=
          llm.scores[key as keyof ConfidenceMetrics] / multiLLMScores.length;
      });
    });

    return {
      consensusCount,
      outlierCount,
      consensusRate: consensusCount / multiLLMScores.length,
      avgScores,
    };
  }, [multiLLMScores]);

  /**
   * Get recommendation for low confidence layers
   */
  const getRecommendation = (layer: ConfidenceLayer, score: number): string => {
    if (score >= layer.threshold) return '';

    const recommendations: Record<string, string> = {
      business_metadata: 'Review extracted name, description, domain, and category fields',
      ui_structure: 'Verify form steps, field definitions, and validation rules',
      primitive_mapping: 'Check primitive type assignments against 16 FERP primitives',
      field_mapping: 'Validate UI-to-primitive bidirectional mappings',
      compliance_detection: 'Confirm regulatory frameworks and audit requirements',
    };

    return recommendations[layer.key] || 'Manual review recommended';
  };

  /**
   * Render layer card
   */
  const renderLayerCard = (layer: ConfidenceLayer): JSX.Element => {
    const score = confidence[layer.key];
    const color = getConfidenceColor(score, layer.threshold);
    const recommendation = getRecommendation(layer, score);
    const needsReview = score < layer.threshold;

    return (
      <Card
        key={layer.key}
        withBorder
        p="md"
        style={{ cursor: onLayerClick ? 'pointer' : 'default' }}
        onClick={() => onLayerClick?.(layer.key)}
      >
        <Group justify="space-between" mb="xs">
          <Group gap="xs">
            <ThemeIcon color={color} variant="light">
              {layer.icon}
            </ThemeIcon>
            <div>
              <Text size="sm" fw={500}>
                {layer.label}
              </Text>
              <Text size="xs" color="dimmed">
                {layer.description}
              </Text>
            </div>
          </Group>
          <Badge color={color} size="lg">
            {(score * 100).toFixed(0)}%
          </Badge>
        </Group>

        <Progress
          value={score * 100}
          color={color}
          size="md"
          mb={needsReview ? 'xs' : undefined}
        />

        {needsReview && (
          <Alert
            icon={<IconAlertCircle size={14} />}
            color="orange"
            variant="light"
            styles={{ message: { fontSize: '0.75rem' } }}
          >
            {recommendation}
          </Alert>
        )}
      </Card>
    );
  };

  /**
   * Render multi-LLM comparison table
   */
  const renderMultiLLMComparison = (): JSX.Element | null => {
    if (!multiLLMScores || multiLLMScores.length === 0) return null;

    return (
      <Card withBorder>
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <ThemeIcon color="blue">
              <IconUsers size={20} />
            </ThemeIcon>
            <Text size="lg" fw={700}>
              Multi-LLM Consensus Analysis
            </Text>
          </Group>
          {consensusAnalysis && (
            <Badge
              color={consensusAnalysis.consensusRate >= 0.8 ? 'green' : 'orange'}
              size="lg"
            >
              {(consensusAnalysis.consensusRate * 100).toFixed(0)}% Consensus
            </Badge>
          )}
        </Group>

        <Table fz="sm">
          <thead>
            <tr>
              <th>LLM</th>
              <th>Overall</th>
              <th>Business</th>
              <th>UI</th>
              <th>Primitives</th>
              <th>Mappings</th>
              <th>Compliance</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {multiLLMScores.map((llm, idx) => (
              <tr key={idx}>
                <td>
                  <Group gap="xs">
                    <ThemeIcon size="sm" color="blue">
                      <IconBrain size={14} />
                    </ThemeIcon>
                    <Text size="sm" fw={500}>
                      {llm.llm_name}
                    </Text>
                  </Group>
                </td>
                <td>
                  <Badge color={getConfidenceColor(llm.scores.overall)}>
                    {(llm.scores.overall * 100).toFixed(0)}%
                  </Badge>
                </td>
                <td>
                  <Badge
                    variant="light"
                    color={getConfidenceColor(llm.scores.business_metadata, 0.85)}
                  >
                    {(llm.scores.business_metadata * 100).toFixed(0)}%
                  </Badge>
                </td>
                <td>
                  <Badge
                    variant="light"
                    color={getConfidenceColor(llm.scores.ui_structure, 0.8)}
                  >
                    {(llm.scores.ui_structure * 100).toFixed(0)}%
                  </Badge>
                </td>
                <td>
                  <Badge
                    variant="light"
                    color={getConfidenceColor(llm.scores.primitive_mapping, 0.9)}
                  >
                    {(llm.scores.primitive_mapping * 100).toFixed(0)}%
                  </Badge>
                </td>
                <td>
                  <Badge
                    variant="light"
                    color={getConfidenceColor(llm.scores.field_mapping, 0.85)}
                  >
                    {(llm.scores.field_mapping * 100).toFixed(0)}%
                  </Badge>
                </td>
                <td>
                  <Badge
                    variant="light"
                    color={getConfidenceColor(llm.scores.compliance_detection, 0.9)}
                  >
                    {(llm.scores.compliance_detection * 100).toFixed(0)}%
                  </Badge>
                </td>
                <td>
                  <ThemeIcon
                    size="sm"
                    color={llm.is_consensus ? 'green' : 'orange'}
                    variant="light"
                  >
                    {llm.is_consensus ? (
                      <IconCheck size={14} />
                    ) : (
                      <IconAlertCircle size={14} />
                    )}
                  </ThemeIcon>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {consensusAnalysis && consensusAnalysis.outlierCount > 0 && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="orange"
            variant="light"
            mt="md"
          >
            <Text size="sm" fw={500}>
              {consensusAnalysis.outlierCount} LLM{consensusAnalysis.outlierCount !== 1 ? 's' : ''}{' '}
              deviated from consensus
            </Text>
            <Text size="xs" color="dimmed">
              Review individual LLM scores for layers with significant disagreement
            </Text>
          </Alert>
        )}
      </Card>
    );
  };

  /**
   * Render overall confidence summary
   */
  const renderOverallSummary = (): JSX.Element => {
    const overallScore = confidence.overall;
    const overallColor = getConfidenceColor(overallScore, 0.85);
    const overallLabel = getConfidenceLabel(overallScore);

    // Count layers below threshold
    const layersBelowThreshold = confidenceLayers.filter(
      layer => confidence[layer.key] < layer.threshold
    ).length;

    return (
      <Grid>
        <Grid.Col span={4}>
          <Card withBorder>
            <Center>
              <RingProgress
                size={180}
                thickness={16}
                sections={[{ value: overallScore * 100, color: overallColor }]}
                label={
                  <Center>
                    <Stack gap={0} ta="center">
                      <Text size="xl" fw={700}>
                        {(overallScore * 100).toFixed(0)}%
                      </Text>
                      <Text size="sm" color="dimmed">
                        Overall
                      </Text>
                      <Badge color={overallColor} mt={4}>
                        {overallLabel}
                      </Badge>
                    </Stack>
                  </Center>
                }
              />
            </Center>
          </Card>
        </Grid.Col>

        <Grid.Col span={8}>
          <Stack gap="xs">
            <Card withBorder p="sm">
              <Group justify="space-between">
                <Text size="sm" fw={500}>
                  Confidence Level
                </Text>
                <Badge color={overallColor} size="lg">
                  {overallLabel} Confidence
                </Badge>
              </Group>
            </Card>

            <Card withBorder p="sm">
              <Group justify="space-between">
                <Text size="sm" fw={500}>
                  Layers Analyzed
                </Text>
                <Badge>{confidenceLayers.length}</Badge>
              </Group>
            </Card>

            <Card withBorder p="sm">
              <Group justify="space-between">
                <Text size="sm" fw={500}>
                  Needs Review
                </Text>
                <Badge color={layersBelowThreshold > 0 ? 'orange' : 'green'}>
                  {layersBelowThreshold} layer{layersBelowThreshold !== 1 ? 's' : ''}
                </Badge>
              </Group>
            </Card>

            {multiLLMScores && consensusAnalysis && (
              <Card withBorder p="sm">
                <Group justify="space-between">
                  <Text size="sm" fw={500}>
                    LLM Consensus
                  </Text>
                  <Badge
                    color={consensusAnalysis.consensusRate >= 0.8 ? 'green' : 'orange'}
                  >
                    {(consensusAnalysis.consensusRate * 100).toFixed(0)}%
                  </Badge>
                </Group>
              </Card>
            )}
          </Stack>
        </Grid.Col>
      </Grid>
    );
  };

  return (
    <Stack gap="lg">
      {/* Overall summary */}
      {renderOverallSummary()}

      <Divider />

      {/* Per-layer breakdown */}
      <div>
        <Group gap="xs" mb="md">
          <ThemeIcon color="blue">
            <IconTrendingUp size={20} />
          </ThemeIcon>
          <Text size="lg" fw={700}>
            Confidence Layer Breakdown
          </Text>
          <Tooltip label="Each layer has a minimum confidence threshold for automatic approval">
            <ActionIcon size="sm" variant="subtle">
              <IconInfoCircle size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
        <Stack gap="md">{confidenceLayers.map(renderLayerCard)}</Stack>
      </div>

      {/* Multi-LLM comparison */}
      {renderMultiLLMComparison()}
    </Stack>
  );
}
